import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import FormNumberFieldV2 from "@components/FormsV2/FormNumberFieldV2/FormNumberFieldV2";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import "./style.css";
import { CURRENCY_TYPE_OPTIONS } from "../types";
import type { CurrencyFormValues } from "../types";

export interface CurrencyFormProps {
  id: string;
  initialValues: CurrencyFormValues;
  isSubmitting: boolean;
  onSubmit: (values: CurrencyFormValues) => void;
}

export const CurrencyForm = ({
  id,
  initialValues,
  isSubmitting,
  onSubmit,
}: CurrencyFormProps): JSX.Element => {
  const [values, setValues] = useState<CurrencyFormValues>(initialValues);

  const set = <K extends keyof CurrencyFormValues>(
    key: K,
    v: CurrencyFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  // Toggling default forces the conversion rate to 1 and locks the field, since
  // the default currency is the basis all other rates are expressed against.
  const handleDefaultToggle = (isDefault: boolean) =>
    setValues((prev) => ({
      ...prev,
      default: isDefault,
      conversionRate: isDefault ? 1 : prev.conversionRate,
    }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSubmit(values);
  };

  const selectedType =
    CURRENCY_TYPE_OPTIONS.find((o) => o.value === values.currencyType) ??
    undefined;

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-sm-3">
          <Form.Label htmlFor="currency-name">Name</Form.Label>
          <TextField
            label={null}
            id="currency-name"
            value={values.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("name", e.target.value)
            }
            required
          />
        </div>
        <div className="col-sm-3">
          <RequiredReactSelect
            label="Format"
            value={selectedType}
            onChange={(opt: any) => set("currencyType", opt?.value ?? "")}
            options={CURRENCY_TYPE_OPTIONS}
            placeholder="Select…"
            required
          />
        </div>
        <div className="col-sm-3">
          <FormNumberFieldV2
            label="Conversion Rate"
            id="currency-conversion-rate"
            value={values.conversionRate}
            min={0}
            step={0.001}
            required
            readOnly={values.default}
            disabled={values.default}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set(
                "conversionRate",
                e.target.value === "" ? null : Number(e.target.value)
              )
            }
            fieldFooter="1.00 of the default currency is equal to ____ of this currency"
          />
        </div>
        <div className="col-sm-3">
          <Form.Label>Default?</Form.Label>
          <div className="currencyDefaultToggle">
            <GenericToggle
              key={`default-${initialValues.default}`}
              initialValueOfIsOn={values.default}
              onToggle={handleDefaultToggle}
              onText="Default"
              offText="Not default"
              hasText
            />
          </div>
          <small className="small text-muted d-block">
            When checked, new projects will initially be assigned this currency.
          </small>
        </div>
      </div>
    </Form>
  );
};

export default CurrencyForm;
